import { ChangeEvent } from "react";
import {
  ProductCategory,
  ProductPricebookItem,
} from "../../../types/product.type";
import AmountInput from "../shared/AmountInput";
import ImageUploadField from "../shared/ImageUploadField";
import { FieldLabel, SectionTitle, inputSx } from "../shared/FormField";
import { STATUS_LABEL } from "../shared/constants";
import ProductPriceBookList from "./ProductPriceBookList";
import {
  DIMENSION_TEXT_MAX_LENGTH,
  FACE_PRICE_FRACTION_DIGITS,
  FDA_NO_MAX_LENGTH,
  ProductForm,
  ProductFormErrors,
} from "./productForm";
import { WEB_IT_EXPORT_LABEL } from "./webItExport";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import Switch from "@mui/material/Switch";
import TextField from "@mui/material/TextField";
import AddIcon from "@mui/icons-material/Add";

const DESCRIPTION_ROWS = 3;
const SEQUENCE_INPUT_PROPS = { inputMode: "numeric" } as const;

const IMMUTABLE_CODE_HINT = "รหัสสินค้าแก้ไขไม่ได้";
const DUPLICATE_CODE_HINT = "ระบบจะออกรหัสสินค้าใหม่ให้อัตโนมัติ";
const EXCLUDE_FROM_WASTE_LABEL = "ไม่นับในยอดของเสีย";

export type ProductTextChangeEvent = ChangeEvent<
  HTMLInputElement | HTMLTextAreaElement
>;

interface ProductDrawerFieldsProps {
  form: ProductForm;
  errors: ProductFormErrors;
  categories: ProductCategory[];
  priceBookItems: ProductPricebookItem[];
  isCodeEditable: boolean;
  /** The copy's code comes from the API, so its hint differs from an edit's. */
  isDuplicate: boolean;
  canCreateCategory: boolean;
  thumbnailFile: File | null;
  pictureFile: File | null;
  onTextChange: (
    field: keyof ProductForm,
  ) => (event: ProductTextChangeEvent) => void;
  onAmountChange: (field: keyof ProductForm) => (value: string) => void;
  onToggleStatus: (
    event: ChangeEvent<HTMLInputElement>,
    checked: boolean,
  ) => void;
  onToggleExport: (
    event: ChangeEvent<HTMLInputElement>,
    checked: boolean,
  ) => void;
  onToggleExcludeFromWaste: (
    event: ChangeEvent<HTMLInputElement>,
    checked: boolean,
  ) => void;
  onSelectThumbnail: (file: File) => void;
  onClearThumbnail: () => void;
  onSelectPicture: (file: File) => void;
  onClearPicture: () => void;
  onAddCategory: () => void;
}

export default function ProductDrawerFields({
  form,
  errors,
  categories,
  priceBookItems,
  isCodeEditable,
  isDuplicate,
  canCreateCategory,
  thumbnailFile,
  pictureFile,
  onTextChange,
  onAmountChange,
  onToggleStatus,
  onToggleExport,
  onToggleExcludeFromWaste,
  onSelectThumbnail,
  onClearThumbnail,
  onSelectPicture,
  onClearPicture,
  onAddCategory,
}: ProductDrawerFieldsProps) {
  const statusLabel = STATUS_LABEL[form.status];
  const resolveCodeHint = () => {
    if (isCodeEditable) return undefined;
    return isDuplicate ? DUPLICATE_CODE_HINT : IMMUTABLE_CODE_HINT;
  };
  const codeHelperText = errors.code || resolveCodeHint();

  return (
    <>
      <SectionTitle>ข้อมูลทั่วไป</SectionTitle>

      <Stack direction="row" flexWrap="wrap" columnGap={3} sx={inputSx}>
        <FormControlLabel
          control={
            <Switch
              checked={form.status === "enable"}
              onChange={onToggleStatus}
            />
          }
          label={statusLabel}
        />
        <FormControlLabel
          control={
            <Switch checked={form.allowExport} onChange={onToggleExport} />
          }
          label={WEB_IT_EXPORT_LABEL}
        />
      </Stack>

      <FieldLabel required={isCodeEditable}>รหัสสินค้า</FieldLabel>
      <TextField
        size="small"
        fullWidth
        value={form.code}
        onChange={onTextChange("code")}
        placeholder="กรอกรหัสสินค้า"
        disabled={!isCodeEditable}
        error={Boolean(errors.code)}
        helperText={codeHelperText}
        sx={inputSx}
      />

      <FieldLabel required>ชื่อรายการ</FieldLabel>
      <TextField
        size="small"
        fullWidth
        value={form.name}
        onChange={onTextChange("name")}
        placeholder="กรอกชื่อรายการสินค้า"
        error={Boolean(errors.name)}
        helperText={errors.name}
        sx={inputSx}
      />

      {/* The add action sits on the label row so it reads as an action ON the field
          rather than as another control below it. */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        gap={1}
      >
        <FieldLabel required>หมวดสินค้า</FieldLabel>
        {canCreateCategory && (
          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={onAddCategory}
            sx={{ mb: 0.75 }}
          >
            เพิ่มหมวดสินค้า
          </Button>
        )}
      </Stack>
      <TextField
        select
        size="small"
        fullWidth
        value={form.categoryCode}
        onChange={onTextChange("categoryCode")}
        error={Boolean(errors.categoryCode)}
        helperText={errors.categoryCode}
        sx={inputSx}
      >
        {categories.map((category) => (
          <MenuItem key={category.uuid} value={category.code}>
            {category.name}
          </MenuItem>
        ))}
      </TextField>

      <FieldLabel>ลำดับสินค้า</FieldLabel>
      <TextField
        size="small"
        fullWidth
        value={form.sequence}
        onChange={onTextChange("sequence")}
        placeholder="กรอกลำดับสินค้า"
        error={Boolean(errors.sequence)}
        helperText={errors.sequence}
        slotProps={{ htmlInput: SEQUENCE_INPUT_PROPS }}
        sx={inputSx}
      />

      <Stack direction={{ xs: "column", sm: "row" }} gap={2} sx={inputSx}>
        <ImageUploadField
          label="รูปภาพสินค้า (150x150px)"
          imageUrl={form.thumbnailUrl}
          file={thumbnailFile}
          onSelect={onSelectThumbnail}
          onClear={onClearThumbnail}
        />
        <ImageUploadField
          label="รูปภาพ Coverage (390x250px)"
          imageUrl={form.pictureUrl}
          file={pictureFile}
          onSelect={onSelectPicture}
          onClear={onClearPicture}
        />
      </Stack>

      <FieldLabel>รายละเอียดสินค้า</FieldLabel>
      <TextField
        size="small"
        fullWidth
        multiline
        rows={DESCRIPTION_ROWS}
        value={form.description}
        onChange={onTextChange("description")}
        placeholder="กรอกรายละเอียดสินค้า"
        sx={inputSx}
      />

      <FieldLabel>แบรนด์</FieldLabel>
      <TextField
        size="small"
        fullWidth
        value={form.brand}
        onChange={onTextChange("brand")}
        placeholder="กรอกชื่อแบรนด์"
        sx={inputSx}
      />

      <Stack direction={{ xs: "column", sm: "row" }} gap={2}>
        <Box sx={{ flex: 1 }}>
          <FieldLabel>บาร์โค้ดชิ้น</FieldLabel>
          <TextField
            size="small"
            fullWidth
            value={form.barcode1}
            onChange={onTextChange("barcode1")}
            placeholder="กรอกบาร์โค้ด"
            sx={inputSx}
          />
        </Box>
        <Box sx={{ flex: 1 }}>
          <FieldLabel>บาร์โค้ดลัง</FieldLabel>
          <TextField
            size="small"
            fullWidth
            value={form.barcode2}
            onChange={onTextChange("barcode2")}
            placeholder="กรอกบาร์โค้ดลัง"
            sx={inputSx}
          />
        </Box>
      </Stack>

      <Stack direction={{ xs: "column", sm: "row" }} gap={2}>
        <Box sx={{ flex: 1 }}>
          <FieldLabel>เลขอ้างอิง</FieldLabel>
          <TextField
            size="small"
            fullWidth
            value={form.reference1}
            onChange={onTextChange("reference1")}
            placeholder="กรอกเลขอ้างอิง"
            sx={inputSx}
          />
        </Box>
        <Box sx={{ flex: 1 }}>
          <FieldLabel>เลขที่ อย.</FieldLabel>
          <TextField
            size="small"
            fullWidth
            value={form.fdaNo}
            onChange={onTextChange("fdaNo")}
            placeholder="กรอกเลขที่ อย."
            slotProps={{ htmlInput: { maxLength: FDA_NO_MAX_LENGTH } }}
            sx={inputSx}
          />
        </Box>
      </Stack>

      <FieldLabel>ขนาด (ซม.)</FieldLabel>
      <TextField
        size="small"
        fullWidth
        value={form.dimensionText}
        onChange={onTextChange("dimensionText")}
        placeholder="เช่น 10 x 6 x 4"
        slotProps={{ htmlInput: { maxLength: DIMENSION_TEXT_MAX_LENGTH } }}
        sx={inputSx}
      />

      <Stack direction={{ xs: "column", sm: "row" }} gap={2}>
        <Box sx={{ flex: 1 }}>
          <FieldLabel>อายุการเก็บรักษา (จำนวนวัน)</FieldLabel>
          <Box sx={inputSx}>
            <AmountInput
              value={form.shelfLifeDays}
              onChange={onAmountChange("shelfLifeDays")}
            />
          </Box>
        </Box>
        <Box sx={{ flex: 1 }}>
          <FieldLabel>น้ำหนัก (กรัม)</FieldLabel>
          <Box sx={inputSx}>
            <AmountInput
              value={form.weightGram}
              onChange={onAmountChange("weightGram")}
            />
          </Box>
        </Box>
      </Stack>

      <FormControlLabel
        sx={inputSx}
        control={
          <Checkbox
            checked={form.excludeFromWaste}
            onChange={onToggleExcludeFromWaste}
          />
        }
        label={EXCLUDE_FROM_WASTE_LABEL}
      />

      <SectionTitle>ราคา</SectionTitle>

      <Stack direction={{ xs: "column", sm: "row" }} gap={2}>
        <Box sx={{ flex: 1 }}>
          <FieldLabel>ราคามาตรฐาน (ใช้จับคู่สินค้าทดแทน)</FieldLabel>
          <Box sx={inputSx}>
            <AmountInput
              value={form.costStd}
              onChange={onAmountChange("costStd")}
            />
          </Box>
        </Box>
        <Box sx={{ flex: 1 }}>
          <FieldLabel required>ราคาหน้าซอง (บาท)</FieldLabel>
          <Box sx={inputSx}>
            <AmountInput
              value={form.facePrice}
              error={errors.facePrice}
              maxFractionDigits={FACE_PRICE_FRACTION_DIGITS}
              onChange={onAmountChange("facePrice")}
            />
          </Box>
        </Box>
      </Stack>

      <ProductPriceBookList items={priceBookItems} />
    </>
  );
}
